import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Info } from "lucide-react";
import {
  fetchRadiantDayTitles,
  fetchRadiantPlayerProfile,
  saveRadiantDisplayedTitleId,
  uploadRadiantPlayerAvatar,
} from "../utils/questApi";
import { QUESTS_API_URL } from "../config/questsApi";
import { QUEST_PROGRESS_CHANGED_EVENT } from "../utils/questProgressReporter";
import { prepareRadiantPlayerAvatar } from "../utils/radiantPlayerAvatar";
import {
  readRadiantDayDisplayedPortrait,
  readRadiantDayStatsScroll,
  readRadiantDayTitlesPanel,
  writeRadiantDayDisplayedPortrait,
  writeRadiantDayStatsScroll,
  writeRadiantDayTitlesPanel,
} from "../utils/radiantDayTitlesPreference";
import { getBlasonByIndex, getBlasonForTitle, getRoturierBlason, orderTitlesForProgressGrid } from "../constants/radiantDayBlasons";
import { QUEST_PROFILE_PORTRAIT_MODES } from "../constants/questPreferences";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import RadiantBlasonDisplay from "./RadiantBlasonDisplay";
import MenuIconTooltip from "./MenuIconTooltip";
import "./RadiantBlasonDisplay.css";
import "./RadiantDayTitlesView.css";

const PANEL_TITLES = "titles";
const PANEL_STATS = "stats";

const STATS_ROWS = [
  { key: "vocabChests", label: "Coffres obtenus au jeu de vocabulaire (niveau en cours, sans aide)" },
  { key: "vocabPerfectScores", label: "Scores parfaits 15/15 au jeu de vocabulaire (niveau en cours, sans aide)" },
  { key: "capturedWords", label: "Mots du registre" },
  { key: "masteredWords", label: "Mots maîtrisés (gradation ≥ B+)" },
  { key: "validatedStandardQuests", label: "Quêtes validées (total)" },
  { key: "relicsObtained", label: "Reliques obtenues (total)" },
  { key: "completeCollections", label: "Collections complètes (total)" },
  { key: "questRewardPotions", label: "Potions gagnées via récompenses de quête" },
  { key: "questRewardGemTypes", label: "Types de pierres gagnés via récompenses de quête" },
  { key: "readingMinutesTotal", label: "Heures de lecture (total)", format: "readingDuration" },
  { key: "readingMinutesMissed", label: "Heures de lecture manquées", format: "readingDuration" },
  { key: "trainingSessionCount", label: "Sessions d'entraînement (total)" },
  { key: "memoryHighScores", label: "Score 14 ou 15 obtenu au jeu de mémoire" },
];

const TITLES_SKELETON_GRADE_LABELS = [
  "Quêteur émérite (2)",
  "Franc-Veneur (0)",
  "Gardien des reliques (2)",
  "Seigneur du vignoble (0)",
  "Chasseur de trésors (4)",
  "Grand lecteur (5)",
  "Apothicaire (3)",
  "Capitaine du vaisseau (2)",
  "Façonneur de joyaux (4)",
  "Quinze Souverain (0)",
  "Mnémosyne (0)",
  "Régent du Trésor (0)",
];
const TITLES_SKELETON_ULTIMATE_LABEL = "Titre Nobiliaire du Royaume (0)";
const ROTURIER_DISPLAYED_GRADE = "Roturier du Royaume";

function formatReadingDuration(minutes) {
  const total = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${hours} H ${String(mins).padStart(2, "0")}`;
}

function trainingSessionsFromMetrics(metrics) {
  if (metrics?.trainingSessionCount != null && metrics.trainingSessionCount !== "") {
    return Math.max(0, Number(metrics.trainingSessionCount) || 0);
  }
  return Math.floor(Math.max(0, Number(metrics?.trainingMinutesTotal) || 0) / 20);
}

function formatStatValue(row, metrics) {
  if (row.format === "readingDuration") {
    return formatReadingDuration(metrics?.[row.key]);
  }
  if (row.key === "trainingSessionCount") {
    return trainingSessionsFromMetrics(metrics).toLocaleString("fr-FR");
  }

  return Number(metrics?.[row.key] ?? 0).toLocaleString("fr-FR");
}

function buildStatsRows(metrics) {
  const rows = STATS_ROWS.map((row) => ({
    key: row.key,
    label: row.label,
    value: formatStatValue(row, metrics),
  }));

  const collections = Array.isArray(metrics?.collectionRelics)
    ? metrics.collectionRelics.slice()
    : [];
  const requiredCollections = [
    { collectionId: "mesopotamian", label: "Reliques de Mésopotamie" },
    { collectionId: "chinese", label: "Reliques de Chine" },
    { collectionId: "persian", label: "Reliques Perses" },
    { collectionId: "medievalMonastery", label: "Reliques médiévales" },
    { collectionId: "indianRelics", label: "Reliques indiennes" },
    { collectionId: "feodalJapanRelics", label: "Reliques japonaises féodales" },
  ];
  let collectionsChanged = false;
  for (const required of requiredCollections) {
    if (!collections.some((collection) => collection.collectionId === required.collectionId)) {
      collections.push({
        collectionId: required.collectionId,
        label: required.label,
        relicsFound: 0,
      });
      collectionsChanged = true;
    }
  }
  if (collectionsChanged) {
    collections.sort((left, right) =>
      String(left.label || "").localeCompare(String(right.label || ""), "fr", {
        sensitivity: "base",
      })
    );
  }
  for (const collection of collections) {
    rows.push({
      key: `collection-${collection.collectionId}`,
      label: `${collection.label} — reliques trouvées`,
      value: Number(collection.relicsFound ?? 0).toLocaleString("fr-FR"),
    });
  }

  return rows;
}

function RadiantStatsScrollbar({
  trackRef,
  scrollbar,
  onTrackPointerDown,
  onThumbPointerDown,
}) {
  return (
    <div
      ref={trackRef}
      className={[
        "radiant-stats-panel__scrollbar",
        !scrollbar.visible && "radiant-stats-panel__scrollbar--hidden",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true"
      onPointerDown={onTrackPointerDown}
    >
      {scrollbar.visible ? (
        <div
          className="radiant-stats-panel__scrollbar-thumb"
          style={{
            height: `${scrollbar.height}px`,
            transform: `translateY(${scrollbar.top}px)`,
          }}
          onPointerDown={onThumbPointerDown}
        />
      ) : null}
    </div>
  );
}

function canSelectTitleForDisplay(title) {
  return (Number(title?.achievedGrade) || 0) >= 1;
}

function resolveDisplayedTitle(titleId, titles, radiantDay) {
  if (titleId) {
    const allTitles = [...titles, ...(radiantDay ? [radiantDay] : [])];
    const match = allTitles.find((title) => title.id === titleId);
    if (match && canSelectTitleForDisplay(match)) return match;
  }

  if (canSelectTitleForDisplay(radiantDay)) return radiantDay;
  return null;
}

function resolveDisplayedGradeLabel(titleId, titles, radiantDay) {
  const match = resolveDisplayedTitle(titleId, titles, radiantDay);
  if (match?.achievedGradeName) return match.achievedGradeName;
  return ROTURIER_DISPLAYED_GRADE;
}

function resolveDisplayedBlasonEntry(titleId, titles, radiantDay) {
  const match = resolveDisplayedTitle(titleId, titles, radiantDay);
  if (match) return getBlasonForTitle(match.id, match.achievedGrade);
  return getRoturierBlason();
}

function hasRadiantTitlesPayload(titles, radiantDay) {
  return Boolean(radiantDay) || (Array.isArray(titles) && titles.length > 0);
}

function RadiantPlayerAvatar({
  dataUrl,
  uploading,
  onUpload,
  profilePortraitMode = QUEST_PROFILE_PORTRAIT_MODES.PHOTO,
  blasonEntry = null,
}) {
  const inputRef = useRef(null);
  const showBlason = profilePortraitMode === QUEST_PROFILE_PORTRAIT_MODES.BLASON;

  const handleOpenPicker = () => {
    if (uploading || showBlason) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await onUpload(file);
  };

  const hasBlason = Boolean(blasonEntry?.imageSrc);
  const hasPhoto = Boolean(dataUrl);

  if (showBlason) {
    return (
      <div
        className="radiant-titles-view__portrait-slot radiant-titles-view__portrait-slot--blason"
        aria-label={
          hasBlason ? `Blason affiché : ${blasonEntry.gradeName}` : "Aucun blason sélectionné"
        }
      >
        <RadiantBlasonDisplay imageSrc={blasonEntry?.imageSrc || null} showLabel={false} />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={[
          "radiant-titles-view__avatar",
          !hasPhoto ? "radiant-titles-view__avatar--empty" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleOpenPicker}
        disabled={uploading}
        aria-label={
          hasPhoto ? "Changer votre photo ou avatar" : "Charger votre photo ou un avatar"
        }
      >
        {hasPhoto ? (
          <img src={dataUrl} alt="" aria-hidden="true" draggable={false} />
        ) : (
          <span className="radiant-titles-view__avatar-placeholder">
            Charger votre photo ou un avatar
          </span>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="radiant-titles-view__avatar-input"
        onChange={handleFileChange}
      />
    </>
  );
}

function RadiantSkelBar({ className }) {
  return <span className={`radiant-titles-skel ${className}`.trim()} />;
}

function RadiantTitleCardSkeleton({ label, ultimate = false }) {
  return (
    <article
      className={`radiant-title-card${ultimate ? " radiant-title-card--ultimate" : ""}`}
    >
      <div className="radiant-title-card__grade-row">
        <label className="radiant-title-card__display-check">
          <span className="radiant-title-card__display-check-box" />
        </label>
        <p
          className={`radiant-title-card__grade${
            ultimate ? " radiant-title-card__grade--ultimate" : ""
          }`}
        >
          <span className="radiant-titles-skel__metrics-wrap">
            {label}
            <RadiantSkelBar className="radiant-titles-skel--grade" />
          </span>
        </p>
      </div>
      <div
        className={`radiant-title-card__bar-track${
          ultimate ? " radiant-title-card__bar-track--ultimate" : ""
        }`}
      />
    </article>
  );
}

function RadiantUltimateGridPlaceholder({ label = TITLES_SKELETON_ULTIMATE_LABEL }) {
  return (
    <div
      className="radiant-titles-view__ultimate-bar-wrap radiant-titles-view__ultimate-bar-wrap--placeholder"
      aria-hidden="true"
    >
      <article className="radiant-title-card radiant-title-card--ultimate">
        <div className="radiant-title-card__grade-row">
          <span className="radiant-title-card__display-check radiant-title-card__display-check--spacer" />
          <p className="radiant-title-card__grade radiant-title-card__grade--ultimate">{label}</p>
        </div>
        <div className="radiant-title-card__bar-track radiant-title-card__bar-track--ultimate" />
      </article>
    </div>
  );
}

function RadiantTitlesPanelSkeleton() {
  return (
    <div className="radiant-titles-view__titles-layout" aria-hidden="true">
      <div className="radiant-titles-view__grid">
        <div className="radiant-titles-view__grid-spacer" aria-hidden="true" />
        <RadiantUltimateGridPlaceholder />
        {TITLES_SKELETON_GRADE_LABELS.map((label) => (
          <RadiantTitleCardSkeleton key={label} label={label} />
        ))}
      </div>
    </div>
  );
}

function RadiantStatsPanelSkeleton() {
  return (
    <div className="radiant-stats-panel" aria-hidden="true">
      <div className="radiant-stats-panel__viewport">
        <div className="radiant-stats-panel__scroller-wrap">
          <div className="radiant-stats-panel__scroll">
            <ul className="radiant-stats-panel__list">
              {STATS_ROWS.slice(0, 8).map((row) => (
                <li key={row.key} className="radiant-stats-panel__row">
                  <span className="radiant-stats-panel__label">
                    {row.label}
                    <RadiantSkelBar className="radiant-titles-skel--stat-label" />
                  </span>
                  <span className="radiant-stats-panel__value">
                    00
                    <RadiantSkelBar className="radiant-titles-skel--stat-value" />
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div
            className="radiant-stats-panel__scrollbar radiant-stats-panel__scrollbar--hidden"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}

function RadiantTitlesHeaderProfile({
  playerName,
  displayedGradeLabel,
  avatarDataUrl,
  avatarUploading,
  onAvatarUpload,
  profilePortraitMode,
  blasonEntry,
  skeleton = false,
}) {
  const trimmedName = String(playerName || "").trim();
  const showBlason = profilePortraitMode === QUEST_PROFILE_PORTRAIT_MODES.BLASON;
  const portraitBlason = blasonEntry || getRoturierBlason();
  const nameMetrics = trimmedName || "Votre nom";

  return (
    <div
      className={[
        "radiant-titles-view__header-profile",
        showBlason ? "radiant-titles-view__header-profile--blason" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {skeleton && showBlason ? (
        <div
          className="radiant-titles-view__portrait-slot radiant-titles-view__portrait-slot--blason"
          aria-hidden="true"
        >
          <RadiantBlasonDisplay
            imageSrc={portraitBlason?.imageSrc || null}
            showLabel={false}
            className="radiant-titles-skel__blason"
          />
        </div>
      ) : skeleton ? (
        <div className="radiant-titles-view__avatar" aria-hidden="true">
          <RadiantSkelBar className="radiant-titles-skel--fill" />
        </div>
      ) : (
        <RadiantPlayerAvatar
          dataUrl={avatarDataUrl}
          uploading={avatarUploading}
          onUpload={onAvatarUpload}
          profilePortraitMode={profilePortraitMode}
          blasonEntry={blasonEntry}
        />
      )}
      <div className="radiant-titles-view__header-identity">
        {skeleton ? (
          <>
            <p
              className={`radiant-titles-view__player-name${
                trimmedName ? "" : " radiant-titles-view__player-name--empty"
              }`}
            >
              {nameMetrics}
              <RadiantSkelBar className="radiant-titles-skel--player-name" />
            </p>
            <p className="radiant-titles-view__displayed-grade">
              {displayedGradeLabel || ROTURIER_DISPLAYED_GRADE}
              <RadiantSkelBar className="radiant-titles-skel--displayed-grade" />
            </p>
          </>
        ) : (
          <>
            {trimmedName ? (
              <p className="radiant-titles-view__player-name">{trimmedName}</p>
            ) : (
              <p className="radiant-titles-view__player-name radiant-titles-view__player-name--empty">
                Votre nom
              </p>
            )}
            {displayedGradeLabel ? (
              <p className="radiant-titles-view__displayed-grade">{displayedGradeLabel}</p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function resolveTitleHeaderLabel(title) {
  if (title.headerLabel) return title.headerLabel;
  const achieved = Number(title.achievedGrade) || 0;
  if (achieved === 0) return `${title.titleName} (0)`;
  return title.currentGradeLabel;
}

function formatTitleMetricNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function sanitizeTitleProgressText(text) {
  if (typeof text !== "string" || !text) return text || "";
  return text.replace(/\d+\.\d+/g, (token) => formatTitleMetricNumber(token));
}

function resolveTitleBarLabel(title) {
  if (title.barLabel) return sanitizeTitleProgressText(title.barLabel);
  if (title.acquired && title.currentGradeName) return title.currentGradeName;
  if (
    title.progressPercent >= 100 &&
    title.targetGradeName &&
    !title.acquired
  ) {
    return title.targetGradeName;
  }
  if (
    title.progressPercent >= 100 &&
    title.currentGradeName &&
    !title.acquired
  ) {
    return title.currentGradeName;
  }
  return sanitizeTitleProgressText(title.progressLabel);
}

function resolveNextGradeLabel(title) {
  if (title.acquired) return "Grade maximal atteint";
  if (title.nextGradeName) return title.nextGradeName;
  if (title.targetGradeName) return title.targetGradeName;
  if ((Number(title.achievedGrade) || 0) === 0 && title.currentGradeName) {
    return title.currentGradeName;
  }
  return "—";
}

export function RadiantTitleCard({
  title,
  barOnly = false,
  ultimate = false,
  displayGradeSelection = false,
  displayGradeSelected = false,
  onDisplayGradeSelect = () => {},
}) {
  const tooltipLines = String(title.tooltip || "")
    .split("\n")
    .filter(Boolean);
  const headerLabel = resolveTitleHeaderLabel(title);
  const barLabel = resolveTitleBarLabel(title);
  const nextGradeLabel = resolveNextGradeLabel(title);
  const displayGradeEnabled = canSelectTitleForDisplay(title);

  const progressBar = (
    <div
      className={`radiant-title-card__bar-track${
        ultimate ? " radiant-title-card__bar-track--ultimate" : ""
      }`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={title.progressInGradeTarget}
      aria-valuenow={title.progressInGradeCurrent}
      aria-label={`${title.titleName} — ${barLabel}`}
    >
      <div
        className={`radiant-title-card__bar-fill${
          ultimate ? " radiant-title-card__bar-fill--ultimate" : ""
        }`}
        style={{ width: `${title.progressPercent}%` }}
      />
      <div className="radiant-title-card__bar-shine" aria-hidden="true" />
      <span
        className={`radiant-title-card__bar-label${
          ultimate ? " radiant-title-card__bar-label--ultimate" : ""
        }`}
      >
        {barLabel}
      </span>
      <MenuIconTooltip
        tooltip={
          <>
            <span className="radiant-title-card__tooltip-line">{title.titleName}</span>
            <span className="radiant-title-card__tooltip-line">
              <span className="radiant-title-card__tooltip-underline">Prochain grade</span>
              {" : "}
              {nextGradeLabel}
            </span>
            {tooltipLines.map((line) => (
              <span key={line} className="radiant-title-card__tooltip-line">
                {line}
              </span>
            ))}
          </>
        }
        placement="above"
        portal
        className="radiant-title-card__bar-info"
      >
        <button
          type="button"
          className="radiant-title-card__bar-info-button"
          aria-label={`À propos : ${title.titleName}`}
        >
          <Info size={13} strokeWidth={2} aria-hidden="true" />
        </button>
      </MenuIconTooltip>
    </div>
  );

  if (barOnly) {
    return (
      <article
        className={`radiant-title-card radiant-title-card--bar-only${
          ultimate ? " radiant-title-card--ultimate" : ""
        }${title.acquired ? " radiant-title-card--acquired" : ""}`}
      >
        {progressBar}
      </article>
    );
  }

  return (
    <article
      className={`radiant-title-card${ultimate ? " radiant-title-card--ultimate" : ""}${
        title.acquired ? " radiant-title-card--acquired" : ""
      }`}
    >
      <div className="radiant-title-card__grade-row">
        {displayGradeSelection && displayGradeEnabled ? (
          <label
            className={`radiant-title-card__display-check${
              displayGradeSelected ? " radiant-title-card__display-check--checked" : ""
            }`}
          >
            <input
              type="checkbox"
              className="radiant-title-card__display-check-input"
              checked={displayGradeSelected}
              onChange={() => onDisplayGradeSelect(title.id)}
              aria-label={`Afficher le grade « ${headerLabel} » sous votre nom`}
            />
            <span className="radiant-title-card__display-check-box" aria-hidden="true" />
          </label>
        ) : displayGradeSelection ? (
          <span
            className="radiant-title-card__display-check radiant-title-card__display-check--spacer"
            aria-hidden="true"
          />
        ) : null}
        <p
          className={`radiant-title-card__grade${
            ultimate ? " radiant-title-card__grade--ultimate" : ""
          }`}
        >
          {headerLabel}
        </p>
      </div>
      {progressBar}
    </article>
  );
}

function RadiantStatsPanel({ metrics }) {
  const scrollRef = useRef(null);
  const persistTimerRef = useRef(null);
  const pendingRestoreRef = useRef(true);
  const {
    trackRef,
    scrollbar,
    updateScrollbar,
    handleThumbPointerDown,
    handleTrackPointerDown,
  } = useCustomScrollbar(scrollRef);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  const rows = useMemo(() => buildStatsRows(metrics), [metrics]);

  const updateScrollUi = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    const threshold = 4;
    const hasOverflow = scrollHeight > clientHeight + 1;

    setScrollFades({
      top: hasOverflow && scrollTop > threshold,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - threshold,
    });

    updateScrollbar();
  }, [updateScrollbar]);

  const flushScroll = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (scrollEl && scrollEl.clientHeight > 0) {
      writeRadiantDayStatsScroll(scrollEl.scrollTop);
    }
  }, []);

  const tryRestoreScroll = useCallback(() => {
    if (!pendingRestoreRef.current) return;

    const scrollEl = scrollRef.current;
    if (!scrollEl || scrollEl.clientHeight === 0) return;

    const savedScrollTop = readRadiantDayStatsScroll();
    if (savedScrollTop > 0) {
      scrollEl.scrollTop = savedScrollTop;
    }

    pendingRestoreRef.current = false;
    updateScrollUi();
  }, [updateScrollUi]);

  const handleScroll = useCallback(() => {
    updateScrollUi();

    if (persistTimerRef.current) {
      window.clearTimeout(persistTimerRef.current);
    }
    persistTimerRef.current = window.setTimeout(() => {
      flushScroll();
    }, 120);
  }, [updateScrollUi, flushScroll]);

  useEffect(() => {
    updateScrollUi();
  }, [rows.length, updateScrollUi]);

  useEffect(() => {
    let frame1;
    let frame2;
    frame1 = window.requestAnimationFrame(() => {
      tryRestoreScroll();
      frame2 = window.requestAnimationFrame(() => tryRestoreScroll());
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
      flushScroll();
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current);
      }
    };
  }, [rows.length, tryRestoreScroll, flushScroll]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return undefined;

    const observer = new ResizeObserver(() => {
      updateScrollUi();
      tryRestoreScroll();
    });

    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [updateScrollUi, tryRestoreScroll]);

  return (
    <div className="radiant-stats-panel">
      <div className="radiant-stats-panel__viewport">
        <div className="radiant-stats-panel__scroller-wrap">
          <div
            ref={scrollRef}
            className="radiant-stats-panel__scroll"
            onScroll={handleScroll}
          >
            <ul className="radiant-stats-panel__list">
              {rows.map((row) => (
                <li key={row.key} className="radiant-stats-panel__row">
                  <span className="radiant-stats-panel__label">{row.label}</span>
                  <span className="radiant-stats-panel__value">{row.value}</span>
                </li>
              ))}
            </ul>
          </div>
          <RadiantStatsScrollbar
            trackRef={trackRef}
            scrollbar={scrollbar}
            onTrackPointerDown={handleTrackPointerDown}
            onThumbPointerDown={handleThumbPointerDown}
          />
        </div>
        {scrollFades.top ? (
          <div className="radiant-stats-panel__fade radiant-stats-panel__fade--top" aria-hidden="true" />
        ) : null}
        {scrollFades.bottom ? (
          <div
            className="radiant-stats-panel__fade radiant-stats-panel__fade--bottom"
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}

export default function RadiantDayTitlesView({
  playerName = "",
  profilePortraitMode = QUEST_PROFILE_PORTRAIT_MODES.PHOTO,
}) {
  const [panel, setPanel] = useState(() => readRadiantDayTitlesPanel());
  const [titles, setTitles] = useState([]);
  const [radiantDay, setRadiantDay] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [profile, setProfile] = useState({ avatarDataUrl: null, displayedTitleId: null });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [displayGradeSaving, setDisplayGradeSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const titleSaveEpochRef = useRef(0);

  const cachedPortrait = useMemo(
    () => readRadiantDayDisplayedPortrait(playerName),
    [playerName]
  );

  const displayedGradeLabel = useMemo(() => {
    if (loading && !hasRadiantTitlesPayload(titles, radiantDay)) {
      return cachedPortrait?.gradeLabel || ROTURIER_DISPLAYED_GRADE;
    }
    if (profile.displayedTitleId) {
      const match = resolveDisplayedTitle(profile.displayedTitleId, titles, radiantDay);
      if (match?.achievedGradeName) return match.achievedGradeName;
      return cachedPortrait?.gradeLabel || ROTURIER_DISPLAYED_GRADE;
    }
    return resolveDisplayedGradeLabel(profile.displayedTitleId, titles, radiantDay);
  }, [
    cachedPortrait,
    loading,
    profile.displayedTitleId,
    radiantDay,
    titles,
  ]);

  const displayedBlasonEntry = useMemo(() => {
    if (loading && !hasRadiantTitlesPayload(titles, radiantDay)) {
      return getBlasonByIndex(cachedPortrait?.blasonIndex) || getRoturierBlason();
    }
    if (profile.displayedTitleId) {
      const match = resolveDisplayedTitle(profile.displayedTitleId, titles, radiantDay);
      if (match) {
        return getBlasonForTitle(match.id, match.achievedGrade) || getRoturierBlason();
      }
      return getBlasonByIndex(cachedPortrait?.blasonIndex) || getRoturierBlason();
    }
    return resolveDisplayedBlasonEntry(profile.displayedTitleId, titles, radiantDay);
  }, [
    cachedPortrait,
    loading,
    profile.displayedTitleId,
    radiantDay,
    titles,
  ]);

  useEffect(() => {
    if (loading) return;
    if (!profile.displayedTitleId) return;
    if (!displayedBlasonEntry || displayedBlasonEntry.index == null) return;
    if (displayedBlasonEntry.index === 0) return;
    writeRadiantDayDisplayedPortrait({
      playerName,
      blasonIndex: displayedBlasonEntry.index,
      gradeLabel: displayedGradeLabel,
    });
  }, [
    displayedBlasonEntry,
    displayedGradeLabel,
    loading,
    playerName,
    profile.displayedTitleId,
  ]);

  const progressGridTitles = useMemo(
    () => orderTitlesForProgressGrid(titles),
    [titles]
  );

  const showHeaderUltimate = panel === PANEL_TITLES && (loading || Boolean(radiantDay));
  const headerUltimateLabel = radiantDay
    ? resolveTitleHeaderLabel(radiantDay)
    : TITLES_SKELETON_ULTIMATE_LABEL;

  const loadTitles = useCallback(async ({ silent = false } = {}) => {
    if (!QUESTS_API_URL) {
      setError("Configurez REACT_APP_QUESTS_API_URL dans .env.local puis redémarrez l'app.");
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }

    const epochAtStart = titleSaveEpochRef.current;

    try {
      const payloadPromise = fetchRadiantDayTitles();
      const profilePromise = silent
        ? Promise.resolve(null)
        : fetchRadiantPlayerProfile().catch(() => null);

      const [payload, profilePayload] = await Promise.all([
        payloadPromise,
        profilePromise,
      ]);
      setTitles(Array.isArray(payload?.titles) ? payload.titles : []);
      setRadiantDay(
        payload?.radiantDay && typeof payload.radiantDay === "object" ? payload.radiantDay : null
      );
      setMetrics(payload?.metrics && typeof payload.metrics === "object" ? payload.metrics : null);
      if (profilePayload && titleSaveEpochRef.current === epochAtStart) {
        setProfile({
          avatarDataUrl: profilePayload?.avatarDataUrl || null,
          displayedTitleId: profilePayload?.displayedTitleId || null,
        });
      }
      setError("");
    } catch (err) {
      setError(err.message || "Impossible de charger les titres.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!profile.displayedTitleId) return;
    if (!hasRadiantTitlesPayload(titles, radiantDay)) return;

    const allTitles = [...titles, ...(radiantDay ? [radiantDay] : [])];
    const match = allTitles.find((title) => title.id === profile.displayedTitleId);
    if (!match || canSelectTitleForDisplay(match)) return;

    titleSaveEpochRef.current += 1;
    setProfile((prev) => ({ ...prev, displayedTitleId: null }));
    writeRadiantDayDisplayedPortrait({
      playerName,
      blasonIndex: 0,
      gradeLabel: ROTURIER_DISPLAYED_GRADE,
    });
    saveRadiantDisplayedTitleId(null).catch((err) => {
      console.error("Réinitialisation du grade affiché :", err);
    });
  }, [loading, titles, radiantDay, profile.displayedTitleId, playerName]);

  const handleAvatarUpload = useCallback(async (file) => {
    setAvatarUploading(true);
    try {
      const dataUrl = await prepareRadiantPlayerAvatar(file);
      const saved = await uploadRadiantPlayerAvatar(dataUrl);
      setProfile((prev) => ({
        ...prev,
        avatarDataUrl: saved?.avatarDataUrl || dataUrl,
      }));
    } catch (err) {
      console.error("Upload du portrait :", err);
    } finally {
      setAvatarUploading(false);
    }
  }, []);

  const handleDisplayGradeSelect = useCallback(
    async (titleId) => {
      if (displayGradeSaving) return;

      const nextTitleId = profile.displayedTitleId === titleId ? null : titleId;
      titleSaveEpochRef.current += 1;
      const saveEpoch = titleSaveEpochRef.current;

      setDisplayGradeSaving(true);
      try {
        const saved = await saveRadiantDisplayedTitleId(nextTitleId);
        if (titleSaveEpochRef.current !== saveEpoch) return;
        const resolvedId = saved?.displayedTitleId ?? nextTitleId;
        setProfile((prev) => ({
          ...prev,
          displayedTitleId: resolvedId,
        }));
        if (!resolvedId) {
          writeRadiantDayDisplayedPortrait({
            playerName,
            blasonIndex: 0,
            gradeLabel: ROTURIER_DISPLAYED_GRADE,
          });
        }
      } catch (err) {
        console.error("Enregistrement du grade affiché :", err);
      } finally {
        setDisplayGradeSaving(false);
      }
    },
    [displayGradeSaving, playerName, profile.displayedTitleId]
  );

  const handlePanelChange = useCallback((nextPanel) => {
    setPanel(nextPanel);
    writeRadiantDayTitlesPanel(nextPanel);
  }, []);

  useEffect(() => {
    loadTitles();

    const handleRefresh = () => {
      loadTitles({ silent: true });
    };

    window.addEventListener(QUEST_PROGRESS_CHANGED_EVENT, handleRefresh);
    return () => window.removeEventListener(QUEST_PROGRESS_CHANGED_EVENT, handleRefresh);
  }, [loadTitles]);

  return (
    <div
      className={`radiant-titles-view${loading ? " radiant-titles-view--skeleton" : ""}`}
      aria-busy={loading}
    >
      <header
        className={[
          "radiant-titles-view__header",
          profilePortraitMode === QUEST_PROFILE_PORTRAIT_MODES.BLASON
            ? "radiant-titles-view__header--blason"
            : "",
          showHeaderUltimate ? "radiant-titles-view__header--with-ultimate" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <RadiantTitlesHeaderProfile
          playerName={playerName}
          displayedGradeLabel={displayedGradeLabel}
          avatarDataUrl={profile.avatarDataUrl}
          avatarUploading={avatarUploading}
          onAvatarUpload={handleAvatarUpload}
          profilePortraitMode={profilePortraitMode}
          blasonEntry={displayedBlasonEntry}
          skeleton={loading}
        />
        {showHeaderUltimate ? (
          <div
            className="radiant-titles-view__header-ultimate"
            aria-label="Titre Nobiliaire du Royaume"
          >
            {loading || !radiantDay ? (
              <RadiantTitleCardSkeleton ultimate label={TITLES_SKELETON_ULTIMATE_LABEL} />
            ) : (
              <RadiantTitleCard
                title={radiantDay}
                ultimate
                displayGradeSelection
                displayGradeSelected={profile.displayedTitleId === radiantDay.id}
                onDisplayGradeSelect={handleDisplayGradeSelect}
              />
            )}
          </div>
        ) : null}
      </header>

      <div
        className={`radiant-titles-view__body${
          panel === PANEL_STATS ? " radiant-titles-view__body--stats" : " radiant-titles-view__body--titles"
        }`}
      >
        {loading ? (
          panel === PANEL_STATS ? (
            <RadiantStatsPanelSkeleton />
          ) : (
            <RadiantTitlesPanelSkeleton />
          )
        ) : null}
        {!loading && error ? (
          <p className="radiant-titles-view__message radiant-titles-view__message--error">{error}</p>
        ) : null}
        {!loading && !error && panel === PANEL_TITLES ? (
          <div className="radiant-titles-view__titles-layout">
            <div className="radiant-titles-view__grid">
              <div className="radiant-titles-view__grid-spacer" aria-hidden="true" />
              <RadiantUltimateGridPlaceholder label={headerUltimateLabel} />
              {progressGridTitles.map((title) => (
                <RadiantTitleCard
                  key={title.id}
                  title={title}
                  displayGradeSelection
                  displayGradeSelected={profile.displayedTitleId === title.id}
                  onDisplayGradeSelect={handleDisplayGradeSelect}
                />
              ))}
            </div>
          </div>
        ) : null}
        {!loading && !error && panel === PANEL_STATS ? (
          <RadiantStatsPanel metrics={metrics} />
        ) : null}
      </div>

      <div
        className="radiant-titles-view__nav"
        role="radiogroup"
        aria-label="Affichage des titres"
      >
        <label className="radiant-titles-view__nav-radio">
          <input
            type="radio"
            name="radiant-day-panel"
            className="radiant-titles-view__nav-radio-input"
            checked={panel === PANEL_TITLES}
            onChange={() => handlePanelChange(PANEL_TITLES)}
            aria-label="Titres"
          />
          <span className="radiant-titles-view__nav-radio-dot" />
        </label>
        <label className="radiant-titles-view__nav-radio">
          <input
            type="radio"
            name="radiant-day-panel"
            className="radiant-titles-view__nav-radio-input"
            checked={panel === PANEL_STATS}
            onChange={() => handlePanelChange(PANEL_STATS)}
            aria-label="Statistiques"
          />
          <span className="radiant-titles-view__nav-radio-dot" />
        </label>
      </div>
    </div>
  );
}
